import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { DecisionHeadline } from "./decisionHeadline";
import styles from "./DecisionRow.module.css";

/**
 * One request waiting for the signed-in approver, in a single line they can read and act on at once: what is asked, a line of supporting
 * facts, and the quick decision buttons. The rest (the full details, amending, reassigning, a reason for rejecting) opens beneath it.
 *
 * @param props.headline - The title and supporting line.
 * @param props.actions - The quick decision buttons.
 * @param props.expanded - Whether the detail is open.
 * @param props.onToggle - Called to open or close the detail.
 * @param props.children - The detail.
 */
export function DecisionRow({
  headline,
  actions,
  expanded,
  onToggle,
  children,
}: {
  headline: DecisionHeadline;
  actions: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const detailId = useId();

  return (
    <li className={expanded ? `${styles.row} ${styles.open}` : styles.row}>
      <div className={styles.line}>
        {/* The text opens the detail too, for a pointer; the chevron is the control for the keyboard and for assistive technology, so a copy button can sit in the text. */}
        <div className={styles.summary} onClick={onToggle}>
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={expanded}
            aria-controls={detailId}
            aria-label={
              expanded
                ? t("myRequests.decision.hideDetail", "Hide details")
                : t("myRequests.decision.showDetail", "Show details")
            }
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <svg
              className={styles.chevron}
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
          <span className={styles.text}>
            <span className={styles.title}>{headline.title}</span>
            <span className={styles.subtitle}>{headline.subtitle}</span>
          </span>
        </div>
        <div className={styles.actions}>{actions}</div>
      </div>
      <div id={detailId} className={styles.detail} hidden={!expanded}>
        {expanded && children}
      </div>
    </li>
  );
}
